import React, { useEffect, useRef, useState } from 'react';
import { FiSearch, FiX } from 'react-icons/fi';
import { useDispatch } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import { getCdnImageUrl } from '../constants/config';
import {
  GLOBAL_SEARCH_TYPES,
  adminSearch,
  entityAdminPath,
  resolveEntityAdminPath,
  searchTypeLabelKey,
} from '../utilities/adminSearch';
import { useChampionships } from '../utilities/championships';
import { useI18n } from '../utilities/i18n';
import { getApiErrorMessage } from '../utilities/stadiumsApi';

export default function GlobalSearch() {
  const dispatch = useDispatch();
  const history = useHistory();
  const { t } = useI18n();
  const { champId } = useChampionships();
  const [type, setType] = useState('all');
  const [query, setQuery] = useState('');
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const timerRef = useRef(null);
  const requestIdRef = useRef(0);
  const hasChamp = Boolean(champId);
  const trimmed = String(query || '').trim();
  const [compact, setCompact] = useState(() => (
    typeof window !== 'undefined' && window.matchMedia('(max-width: 860px)').matches
  ));

  useEffect(() => {
    const onDocClick = (event) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  useEffect(() => {
    const media = window.matchMedia('(max-width: 860px)');
    const apply = () => {
      const next = media.matches;
      setCompact(next);
      if (!next) setPanelOpen(false);
    };
    apply();
    if (media.addEventListener) media.addEventListener('change', apply);
    else media.addListener(apply);
    return () => {
      if (media.removeEventListener) media.removeEventListener('change', apply);
      else media.removeListener(apply);
    };
  }, []);

  useEffect(() => {
    setItems([]);
    setError('');
    setOpen(false);
  }, [champId]);

  useEffect(() => {
    if (!compact || !panelOpen) return undefined;
    const id = window.requestAnimationFrame(() => {
      if (inputRef.current) inputRef.current.focus();
    });
    return () => window.cancelAnimationFrame(id);
  }, [compact, panelOpen]);

  useEffect(() => {
    if (!open || !hasChamp) return undefined;
    if (compact && !panelOpen) return undefined;
    if (!trimmed) {
      setItems([]);
      setError('');
      setLoading(false);
      return undefined;
    }
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      loadItems(trimmed);
    }, 250);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [query, open, hasChamp, type, champId, compact, panelOpen]);

  const closePanel = () => {
    setPanelOpen(false);
    setOpen(false);
    setQuery('');
    setItems([]);
    setError('');
  };

  const loadItems = async (search) => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError('');
    try {
      const page = await adminSearch(dispatch, {
        type: type === 'all' ? undefined : type,
        types: type === 'all' ? GLOBAL_SEARCH_TYPES : undefined,
        search,
        champId,
        limit: 20,
      });
      if (requestId !== requestIdRef.current) return;
      setItems(page.items.filter((item) => GLOBAL_SEARCH_TYPES.includes(item.type)));
    } catch (err) {
      if (requestId !== requestIdRef.current) return;
      setItems([]);
      setError(getApiErrorMessage(err, t('searchLoadError')));
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  };

  const closeSearch = () => {
    setQuery('');
    setItems([]);
    setOpen(false);
    if (compact) setPanelOpen(false);
  };

  const syncItemPath = (item) => {
    const path = entityAdminPath(item);
    if (!path) return '';
    if (item?.type === 'league' && path === '/schedule_management') return '';
    return path;
  };

  const goToItem = async (item) => {
    const path = await resolveEntityAdminPath(dispatch, item);
    if (!path) return;
    closeSearch();
    history.push(path);
  };

  const placeholder = !hasChamp
    ? t('globalSearchNeedChamp')
    : (compact ? t('globalSearchPhShort') : t('globalSearchPh'));

  if (compact && !panelOpen) {
    return (
      <div className="global-search is-collapsed" ref={wrapRef}>
        <button
          type="button"
          className="global-search-toggle"
          aria-label={t('globalSearchOpen')}
          onClick={() => setPanelOpen(true)}
        >
          <FiSearch size="1.2em" color="#edf5ff" />
        </button>
      </div>
    );
  }

  return (
    <div
      className={`global-search${open ? ' is-open' : ''}${panelOpen ? ' is-panel-open' : ''}${hasChamp ? '' : ' is-disabled'}`}
      ref={wrapRef}
      role="search"
      aria-label={t('globalSearchLabel')}
    >
      <div className="global-search-row">
        {compact ? (
          <button
            type="button"
            className="global-search-close"
            aria-label={t('globalSearchClose')}
            onClick={closePanel}
          >
            <FiX size="1.15em" />
          </button>
        ) : (
          <span className="global-search-label" aria-hidden="true">
            <FiSearch className="global-search-icon" />
          </span>
        )}
        <select
          className="global-search-type"
          value={type}
          disabled={!hasChamp}
          aria-label={t('globalSearchType')}
          onChange={(event) => {
            setType(event.target.value);
            setOpen(true);
            if (inputRef.current) inputRef.current.focus();
          }}
        >
          <option value="all">{t('searchTypeAll')}</option>
          {GLOBAL_SEARCH_TYPES.map((itemType) => (
            <option key={itemType} value={itemType}>{t(searchTypeLabelKey(itemType))}</option>
          ))}
        </select>
        <div className="global-search-field">
          <input
            ref={inputRef}
            id="global-search-input"
            type="text"
            className="global-search-input"
            placeholder={placeholder}
            value={query}
            disabled={!hasChamp}
            onFocus={() => setOpen(true)}
            onChange={(event) => {
              setQuery(event.target.value);
              setError('');
              setOpen(true);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                if (items[0]) goToItem(items[0]);
              }
              if (event.key === 'Escape') {
                if (compact && panelOpen) closePanel();
                else setOpen(false);
              }
            }}
          />
          {query ? (
            <button
              type="button"
              className="global-search-clear"
              onClick={() => {
                setQuery('');
                setItems([]);
                setError('');
                if (inputRef.current) inputRef.current.focus();
              }}
            >
              ×
            </button>
          ) : null}
        </div>
      </div>

      {open && hasChamp ? (
        <div className="global-search-dropdown">
          {loading ? <div className="global-search-empty">{t('searchLoading')}</div> : null}
          {!loading && items.map((item) => {
            const image = getCdnImageUrl(item.image, item.type);
            const typeKey = searchTypeLabelKey(item.type);
            const href = syncItemPath(item);
            const content = (
              <>
                {image ? <img className="global-search-option-image" src={image} alt="" /> : null}
                {typeKey ? <span className="global-search-option-type">{t(typeKey)}</span> : null}
                <span className={`global-search-option-text${item.subtitle ? ' has-subtitle' : ''}`}>
                  {item.subtitle ? <small>{item.subtitle}</small> : null}
                  <span className="global-search-option-title">{item.name}</span>
                </span>
              </>
            );
            if (href) {
              return (
                <Link
                  key={`${item.type}-${item.id}`}
                  to={href}
                  className="global-search-option"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={closeSearch}
                >
                  {content}
                </Link>
              );
            }
            return (
              <button
                key={`${item.type}-${item.id}`}
                type="button"
                className="global-search-option"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => goToItem(item)}
              >
                {content}
              </button>
            );
          })}
          {!loading && !trimmed ? (
            <div className="global-search-empty">{t('globalSearchHint')}</div>
          ) : null}
          {!loading && trimmed && !items.length ? (
            <div className="global-search-empty">{error || t('searchEmpty')}</div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
